/* eslint-disable @typescript-eslint/member-ordering */
// https://firebase.google.com/docs/auth/web/google-signin?hl=fr
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ExpiryTimeStorage } from '@aodocs/utils-storage';
import { sendEmailVerification, User } from '@firebase/auth';
import { FirebaseApp, getApp, initializeApp } from 'firebase/app';
import {
  Auth,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  UserCredential,
} from 'firebase/auth';
import * as firebaseui from 'firebaseui';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { BehaviorSubject, catchError, from, map, Observable, of } from 'rxjs';

import { AUTHENTICATION_CONFIG } from '../injection-tokens/config.token';
import { AuthenticationConfig } from '../models/authentication-config.model';
import { ApiAuthenticationProviderTypes, AuthenticationProvider } from '../models/provider.enum';
import { StoredTokenModel } from '../models/stored-token.model';
import { AuthenticationUser, MicrosoftUserCredential } from '../models/user.model';

const DEFAULT_APP_NAME = 'AODOCS-AUTH';
const DEFAULT_SCOPES: string[] = ['openid', 'email', 'profile'];
const ONE_MINUTE: number = 60 * 1000;
const ONE_HOUR: number = ONE_MINUTE * 60;
const FIVE_MINUTES: number = ONE_MINUTE * 5;

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly auth: Auth;
  currentAuthProvider: AuthenticationProvider | null = null;

  private readonly _user$ = new BehaviorSubject<AuthenticationUser | null>(null);
  user$: Observable<AuthenticationUser | null> = this._user$.asObservable();

  private readonly _pendingRedirect$ = new BehaviorSubject(true);
  pendingRedirect$: Observable<boolean> = this._pendingRedirect$.asObservable();
  private readonly _error$ = new BehaviorSubject<unknown>(null);
  error$: Observable<unknown> = this._error$.asObservable();

  constructor(
    @Inject(AUTHENTICATION_CONFIG) private readonly authenticationConfig: AuthenticationConfig,
    private readonly httpClient: HttpClient
  ) {
    let app: FirebaseApp;
    try {
      app = getApp(authenticationConfig.authAppName || DEFAULT_APP_NAME);
    } catch {
      app = initializeApp(authenticationConfig, authenticationConfig.authAppName || DEFAULT_APP_NAME);
    }
    this.auth = getAuth(app);
    onAuthStateChanged(this.auth, async (user) => {
      if (user && (user.providerData[0].providerId === AuthenticationProvider.PASSWORD ? user.emailVerified : true)) {
        this.currentAuthProvider = user.providerData[0].providerId as AuthenticationProvider;
        const token = await user.getIdToken();
        const decodeToken: { primaryEmail?: string } = jwtDecode(token);
        this._user$.next({
          email: user.email,
          primaryEmail: decodeToken.primaryEmail,
          photoURL: user.photoURL,
          displayName: user.displayName,
        });
      } else {
        this._user$.next(null);
        this.currentAuthProvider = null;
        ExpiryTimeStorage.remove(this.authenticationConfig.storageKey);
      }
      this._pendingRedirect$.next(false);
    });

    getRedirectResult(this.auth)
      .then((result) => {
        this._error$.next(null);
        if (result) {
          this.currentAuthProvider = result.providerId as AuthenticationProvider;
          this.handleSignInResult(result, []);
        }
      })
      .catch((error) => {
        this._error$.next(error);
      });
  }

  signOut(): Promise<void> {
    return this.auth.signOut();
  }

  authenticate(authProvider: AuthenticationProvider, login_hint = '', additionalScopes?: string[]): Promise<void> {
    const provider: GoogleAuthProvider | OAuthProvider =
      authProvider === AuthenticationProvider.GOOGLE ? new GoogleAuthProvider() : new OAuthProvider(authProvider);
    DEFAULT_SCOPES.forEach((scope) => provider.addScope(scope));
    additionalScopes = additionalScopes?.filter((item) => !new Set(DEFAULT_SCOPES).has(item));
    additionalScopes?.forEach((scope) => provider.addScope(scope));
    provider.setCustomParameters({ login_hint });
    this.currentAuthProvider = authProvider;
    return this.signInWithPopup(provider, additionalScopes || []);
  }

  authorize(scopes: string[]): void {
    const provider: GoogleAuthProvider | OAuthProvider =
      this.currentAuthProvider === AuthenticationProvider.GOOGLE ? new GoogleAuthProvider() : new OAuthProvider('microsoft.com');
    [...DEFAULT_SCOPES, ...scopes].forEach((scope) => provider.addScope(scope));
    if (this._user$.value?.email) {
      provider.setCustomParameters({ login_hint: this._user$.value.email });
    }
    this.signInWithPopup(provider, scopes);
  }

  getAuthProvider(domain: string | undefined): Observable<ApiAuthenticationProviderTypes[]> {
    if (!this.authenticationConfig.authProviderUrl || !domain) return of(this.filterAvailableProviders([]));

    return this.httpClient
      .get<{ providers: ApiAuthenticationProviderTypes[] }>(this.authenticationConfig.authProviderUrl, {
      params: { 'aodocs-domain': domain },
    })
      .pipe(
        map((response) => this.filterAvailableProviders(response.providers)),
        catchError(() => of(this.filterAvailableProviders([])))
      );
  }

  signInWithCredential(accessToken: string, additionalScopes?: string[]): Promise<void> {
    const credential = GoogleAuthProvider.credential(null, accessToken);
    const scopes = additionalScopes?.filter((item) => !new Set(DEFAULT_SCOPES).has(item)) || [];
    return signInWithCredential(this.auth, credential)
      .then((result: UserCredential) => {
        this.handleSignInResult(result, scopes);
      })
      .catch((error) => this._error$.next(error));
  }

  getIdToken(forceRefresh = false): Observable<string | null> {
    if (this.auth.currentUser) {
      return from(this.auth.currentUser.getIdToken(forceRefresh));
    }
    return of(null);
  }

  getAccessToken(): string | null {
    const storedToken = ExpiryTimeStorage.get<StoredTokenModel>(this.authenticationConfig.storageKey);
    return storedToken ? storedToken.token : null;
  }

  isAuthorized(scopes: string[]): boolean {
    const storedToken = ExpiryTimeStorage.get<StoredTokenModel>(this.authenticationConfig.storageKey);
    return !!storedToken && storedToken.scopes && scopes.every((scope) => storedToken.scopes.includes(scope));
  }

  /**
   * Limited access token is used for application not yet authenticated with firebase but needs direct access to the session token.
   * If the user is already authenticated with firebase, the token will be stored in the main storage key.
   * Otherwise, it will be stored in a separate storage key with 5 minutes expiration.
   */
  getLimitedAccessToken(): string | null {
    const accessToken = this.getAccessToken();
    if (accessToken) return accessToken;
    const storedToken = ExpiryTimeStorage.get<StoredTokenModel>(this.authenticationConfig.storageKey + '-limited');
    return storedToken ? storedToken.token : null;
  }

  setLimitedAccessToken(token: string, additionalScopes?: string[]): void {
    const scopes = additionalScopes ? Array.from(new Set([...DEFAULT_SCOPES, ...additionalScopes])) : DEFAULT_SCOPES;
    ExpiryTimeStorage.set<StoredTokenModel>(
      this.authenticationConfig.storageKey + '-limited',
      {
        token,
        scopes: scopes,
      },
      FIVE_MINUTES
    );
  }

  getIdTokenExpiredDate(): Observable<number> {
    return this.getIdToken().pipe(
      map((idToken) => {
        let token: JwtPayload | undefined;

        try {
          token = jwtDecode(idToken ?? '');
        } catch (e) {
          return Date.now();
        }

        return token?.exp ? token.exp * 1000 : Date.now();
      })
    );
  }

  generateFirebaseUI(): firebaseui.auth.AuthUI {
    return new firebaseui.auth.AuthUI(this.auth);
  }

  async sendConfirmationEmail(user: User): Promise<void> {
    await this.signOut();
    try {
      await sendEmailVerification(user);
    } catch (e) {
      this._error$.next(e);
    }
  }

  private filterAvailableProviders(providers: ApiAuthenticationProviderTypes[]): ApiAuthenticationProviderTypes[] {
    if (this.authenticationConfig.limitProvidersTo?.length) {
      const availableProviders = providers.filter((provider) => this.authenticationConfig.limitProvidersTo?.includes(provider));
      return availableProviders.length ? availableProviders : this.authenticationConfig.limitProvidersTo;
    }

    return providers;
  }

  private signInWithPopup(provider: GoogleAuthProvider, scopes: string[]): Promise<void> {
    return signInWithPopup(this.auth, provider)
      .then((result) => {
        this.handleSignInResult(result, scopes);
      })
      .catch((error) => this._error$.next(error));
  }

  private handleSignInResult(result: UserCredential, scopes: string[]): void {
    this._error$.next(null);
    if (this.currentAuthProvider === AuthenticationProvider.GOOGLE) {
      const credential = GoogleAuthProvider.credentialFromResult(result);
      if (credential?.accessToken) {
        this.storeToken(credential.accessToken, scopes);
      }
    } else if (this.currentAuthProvider === AuthenticationProvider.MICROSOFT) {
      const token = (result.user as unknown as MicrosoftUserCredential).accessToken;
      if (token) {
        this.storeToken(token, scopes);
      }
    }
  }

  private storeToken(token: string, scopes: string[]): void {
    ExpiryTimeStorage.set<StoredTokenModel>(
      this.authenticationConfig.storageKey,
      {
        token,
        scopes: [...DEFAULT_SCOPES, ...scopes],
      },
      ONE_HOUR
    );
  }
}
